import { type AttributeValue, attributes, type HtmlValue, html, joinClasses } from 'BomdisoftComponents/html/index';

export type SliderVariant = 'channel' | 'hairline' | 'notched';

export type SliderOptions = {
  attributes?: Readonly<Record<string, AttributeValue>>;
  className?: string;
  disabled?: boolean;
  id: string;
  label?: HtmlValue;
  max?: number;
  min?: number;
  readoutLabel?: string;
  step?: number;
  value: number;
  variant?: SliderVariant;
};

export const SLIDER_NOTCH_COUNT = 20;

export const clampSliderValue = (raw: number, min: number, max: number, step: number): number => {
  if (Number.isNaN(raw)) {
    return min;
  }
  const snapped = min + Math.round((raw - min) / step) * step;
  return Math.min(max, Math.max(min, snapped));
};

export const toSliderPercent = (value: number, min: number, max: number) => (max === min ? 0 : ((value - min) / (max - min)) * 100);

export const toLitNotches = (percent: number) => Math.round((percent / 100) * SLIDER_NOTCH_COUNT);

export const renderSlider = ({ attributes: extra = {}, className, disabled, id, label, max = 100, min = 0, readoutLabel = 'Value', step = 1, value, variant = 'channel' }: SliderOptions) => {
  const percent = toSliderPercent(value, min, max);
  const litNotches = toLitNotches(percent);
  const notches =
    variant === 'notched' ? html`<span aria-hidden="true" class="bomdisoft-slider-notches">${Array.from({ length: SLIDER_NOTCH_COUNT }, (_, index) => html`<span${attributes({ class: 'bomdisoft-slider-notch', 'data-notch': true, 'data-notch-on': index < litNotches })}></span>`)}</span>` : '';
  const control = html`<span${attributes({ class: joinClasses('bomdisoft-slider', !label && className), 'data-disabled': disabled, 'data-variant': variant, style: `--slider-value: ${percent}%` })}><span class="bomdisoft-slider-track">${notches}<input${attributes({ ...extra, class: 'bomdisoft-slider-range', disabled, id, max, min, step, type: 'range', value })}></span><input${attributes({ 'aria-label': readoutLabel, class: 'bomdisoft-slider-readout', disabled, inputmode: 'numeric', value: String(value) })}></span>`;
  if (!label) return control;
  return html`<label${attributes({ class: joinClasses('bomdisoft-slider-label', className), 'data-disabled': disabled, for: id })}><span class="bomdisoft-slider-label-text">${label}</span>${control}</label>`;
};
